import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import microscopyImage from '../assets/7_537.oir Group_1 Level_1 Area_1.jpg';
import nitrousImage from '../assets/nitrous psychedelic image.png';
import psilocybinPDF from '../assets/s41593-025-02068-0.pdf';
import nitrousVideo from '../assets/Nitrous movie.mp4';

const ResearchDetails: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const videoRef = useRef(null);
  const isVideoInView = useInView(videoRef, { once: true, margin: '-100px' });

  return (
    <>
      <section
        ref={ref}
        className="pt-20 md:pt-28 pb-12 md:pb-16 bg-neutral-light"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-penn-blue mb-6 text-center">
              Research Highlights
            </h2>
            <div className="w-24 h-1 bg-penn-red mx-auto mb-12"></div>

            {/* Nitrous Oxide Research */}
            <div className="mb-16">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden border border-neutral-medium"
              >
                <div className="md:flex">
                  <div className="md:w-1/2 p-8 md:p-12">
                    <h3 className="text-2xl md:text-3xl font-bold text-penn-blue mb-4">
                      Nitrous Oxide & Rapid Antidepressant Effects
                    </h3>
                    <p className="text-neutral-dark leading-relaxed mb-4">
                      Dr. Cichon has an NIH R35 grant focused on elucidating the cellular and circuit
                      mechanisms by which inhaled nitrous oxide exerts rapid antidepressant effects
                      in rodent models of chronic stress. To map the input-output architecture of
                      these neurons and their downstream mood-related circuits, we employ high-resolution
                      2-photon anatomical imaging and have performed over 600 in vivo imaging experiments.
                    </p>
                    <p className="text-neutral-dark leading-relaxed mb-4">
                      Our research reveals that nitrous oxide rapidly activates deep-layer excitatory
                      pyramidal neurons, particularly layer 5 (L5) neurons in the prefrontal cortex.
                      Using Cre-driver lines and chemogenetic tools, we have demonstrated that L5 neurons
                      are both necessary and sufficient for mediating nitrous oxide's antidepressant-like effects.
                    </p>
                    <p className="text-neutral-dark leading-relaxed">
                      These neurons are also modulated by serotonergic psychedelics via 5HT-2A receptors,
                      suggesting a potential shared mechanism between anesthetics and psychedelics. This work
                      has been recently published in <span className="font-semibold text-penn-red"><a href="https://cmsdev1.pmacs.upenn.edu/cichon-lab/assets/user-content/documents/2025-nat-communications-nitrous.pdf" target="_blank" rel="noopener noreferrer">Nature Communications</a></span>.
                    </p>
                  </div>
                  <div className="md:w-1/2 p-8 bg-gradient-to-br from-penn-blue/10 to-penn-red/10 flex items-center justify-center">
                    <img
                      src={nitrousImage}
                      alt="Nitrous oxide research visualization"
                      className="w-full h-auto rounded-lg shadow-md"
                    />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Psilocybin Research */}
            <div className="mb-16">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden border border-neutral-medium"
              >
                <div className="md:flex flex-row-reverse">
                  <div className="md:w-1/2 p-8 md:p-12">
                    <h3 className="text-2xl md:text-3xl font-bold text-penn-blue mb-4">
                      Psilocybin & Chronic Pain
                    </h3>
                    <p className="text-neutral-dark leading-relaxed mb-4">
                      Recent studies have shown in two chronic pain models that a single dose of psilocybin
                      induces a rapid and sustained reversal of both mechanical allodynia and anxiodepression-like
                      states in adult male and female mice. Using local psilocin injections, the key active
                      metabolite of psilocybin, we show that the engagement of prefrontal cortical circuits is
                      critical for the concurrent alleviation of both conditions.
                    </p>
                    <p className="text-neutral-dark leading-relaxed mb-4">
                      Two-photon calcium imaging reveals that psilocin rapidly normalizes chronic pain-associated
                      hyperactivity in anterior cingulate cortex layer 2/3 pyramidal neurons. Pharmacologic
                      manipulations with full agonists of 5-HT2A and 5-HT1A receptors replicated some, but not all,
                      of psilocin's cellular and behavioral effects.
                    </p>
                    <p className="text-neutral-dark leading-relaxed">
                      This suggests that psilocin's actions arise from partial agonism at these receptors within
                      shared circuits governing pain and mood processing. This work was recently published in{' '}
                      <span className="font-semibold text-penn-red"><a href={psilocybinPDF} target="_blank" rel="noopener noreferrer">Nature Neuroscience</a></span>.
                    </p>
                  </div>
                  <div className="md:w-1/2 p-8 bg-gradient-to-br from-penn-red/10 to-penn-blue/10 flex items-center justify-center">
                    <img
                      src={microscopyImage}
                      alt="Two-photon microscopy image of layer 5 neuron"
                      className="w-full h-auto rounded-lg shadow-md"
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Section */}
      <section
        ref={videoRef}
        className="pt-12 md:pt-16 pb-20 md:pb-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVideoInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-penn-blue mb-6">
              Nitrous Oxide Visualization
            </h2>
            <div className="w-24 h-1 bg-penn-red mx-auto"></div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isVideoInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-5xl mx-auto"
          >
            <div className="relative w-full rounded-lg overflow-hidden shadow-xl border-2 border-neutral-medium bg-gradient-to-br from-penn-blue/5 to-penn-red/5">
              <video
                controls
                className="w-full h-auto"
                style={{ maxHeight: '600px', display: 'block' }}
                preload="metadata"
                playsInline
              >
                <source src={nitrousVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ResearchDetails;

